import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { getCsrfToken, getCurrentSurvey, submitCurrentSurvey } from "../api";
import { trackProductEvent } from "../analytics/trackEvent";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import DesktopRightRail from "../components/DesktopRightRail";
import {
  SURVEYS_CHOICE_QUESTION_TYPES,
  loadSurveyContent,
  normalizeSurveyContentKey,
} from "../utils/surveyContent";
import "../styles/pages/Surveys.css";

function getDefaultAnswer(question) {
  if (question.type === "multiple_choice") {
    return [];
  }

  return "";
}

function buildAnswerState(questions, currentAnswers = {}) {
  const nextAnswers = {};

  questions.forEach((question) => {
    const currentValue = currentAnswers[question.id];

    if (question.type === "multiple_choice") {
      nextAnswers[question.id] = Array.isArray(currentValue)
        ? currentValue.filter((value) =>
            question.options.some((option) => option.value === value)
          )
        : [];
      return;
    }

    if (question.type === "single_choice") {
      const normalizedValue = String(currentValue || "").trim();
      nextAnswers[question.id] = question.options.some(
        (option) => option.value === normalizedValue
      )
        ? normalizedValue
        : "";
      return;
    }

    nextAnswers[question.id] = typeof currentValue === "string" ? currentValue : "";
  });

  return nextAnswers;
}

function hasSurveyAnswerValue(answers) {
  return Object.values(answers || {}).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return String(value || "").trim().length > 0;
  });
}

function isQuestionAnswered(question, answers) {
  const value = answers[question.id];

  if (question.type === "multiple_choice") {
    return Array.isArray(value) && value.length > 0;
  }

  return String(value || "").trim().length > 0;
}

function getQuestionLimitError(question, t) {
  const maxSelections = Number(question?.maxSelections || 0);
  if (!Number.isInteger(maxSelections) || maxSelections < 1) {
    return "";
  }

  return (
    String(question?.validationMessage || "").trim() ||
    t("surveys_selection_limit", {
      defaultValue: "You can select up to {{count}} options.",
      count: maxSelections,
    })
  );
}

function getQuestionValidationError(question, answers, t) {
  if (question.required && !isQuestionAnswered(question, answers)) {
    return t("surveys_submit_required");
  }

  if (question.type === "multiple_choice") {
    const value = answers[question.id];
    const maxSelections = Number(question.maxSelections || 0);
    if (
      Array.isArray(value) &&
      Number.isInteger(maxSelections) &&
      maxSelections > 0 &&
      value.length > maxSelections
    ) {
      return getQuestionLimitError(question, t);
    }
  }

  return "";
}

function buildQuestionErrors(questions, answers, t) {
  const nextErrors = {};

  questions.forEach((question) => {
    const error = getQuestionValidationError(question, answers, t);
    if (error) {
      nextErrors[question.id] = error;
    }
  });

  return nextErrors;
}

function formatSubmittedAt(submittedAt, language) {
  if (!submittedAt) {
    return "";
  }

  const parsed = new Date(submittedAt);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  try {
    return parsed.toLocaleString(language || undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch (error) {
    return parsed.toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }
}

function getSurveyErrorMessage(t, error, fallbackKey) {
  const detail = String(error?.message || "").trim();

  switch (detail) {
    case "survey_not_found":
      return t("surveys_empty_body");
    case "survey_already_submitted":
      return t("surveys_submit_already_submitted");
    case "surveys_unavailable":
      return t("surveys_load_error_body");
    case "surveys_endpoint_not_found":
      return t("surveys_backend_not_live_body");
    case "survey_content_unavailable":
      return t("surveys_load_error_body");
    case "login_required":
      return t("surveys_auth_required_body");
    default:
      return t(fallbackKey);
  }
}

function shouldShowSurveyErrorMeta(errorMessage) {
  const knownErrorCodes = new Set([
    "survey_not_found",
    "survey_already_submitted",
    "surveys_unavailable",
    "surveys_endpoint_not_found",
    "survey_content_unavailable",
    "login_required",
  ]);

  return Boolean(errorMessage) && !knownErrorCodes.has(String(errorMessage).trim());
}

function getCountBucket(count) {
  if (count <= 0) return "0";
  if (count <= 3) return "1-3";
  if (count <= 10) return "4-10";
  return "11+";
}

function SurveyQuestionCard({
  question,
  index,
  t,
  preview = false,
  value,
  errorMessage = "",
  disabled = false,
  onChange,
}) {
  const questionNumber = index + 1;
  const inputName = `survey-question-${question.id}`;
  const normalizedValue =
    value === undefined ? getDefaultAnswer(question) : value;

  const handleSingleChoiceChange = (nextValue) => {
    if (typeof onChange === "function") {
      onChange(question, nextValue);
    }
  };

  const handleMultipleChoiceToggle = (nextValue, checked) => {
    const currentValues = Array.isArray(value) ? value : [];
    const nextValues = checked
      ? [...new Set([...currentValues, nextValue])]
      : currentValues.filter((entry) => entry !== nextValue);

    if (typeof onChange === "function") {
      onChange(question, nextValues);
    }
  };

  const handleTextChange = (nextValue) => {
    if (typeof onChange === "function") {
      onChange(question, nextValue);
    }
  };

  return (
    <article className="surveys-question-card">
      <div className="surveys-question-card__header">
        <div className="surveys-question-card__number">{questionNumber}</div>
        <div className="surveys-question-card__title-wrap">
          <h3 className="surveys-question-card__title">{question.label}</h3>
          {question.helperText ? (
            <p className="surveys-question-card__helper">{question.helperText}</p>
          ) : null}
          {question.required ? (
            <span className="surveys-question-card__required">
              {t("surveys_question_required")}
            </span>
          ) : null}
        </div>
      </div>

      {SURVEYS_CHOICE_QUESTION_TYPES.has(question.type) ? (
        <div className="surveys-question-card__options" role="group">
          {question.options.map((option) => {
            const inputType =
              question.type === "multiple_choice" ? "checkbox" : "radio";
            const checked =
              question.type === "multiple_choice"
                ? Array.isArray(normalizedValue) &&
                  normalizedValue.includes(option.value)
                : normalizedValue === option.value;

            return (
              <label
                key={`${question.id}-${option.value}`}
                className={`surveys-question-card__option${
                  preview || disabled
                    ? ""
                    : " surveys-question-card__option--interactive"
                }`}
              >
                <input
                  type={inputType}
                  name={inputName}
                  value={option.value}
                  checked={checked}
                  disabled={preview || disabled}
                  onChange={(event) => {
                    if (question.type === "multiple_choice") {
                      handleMultipleChoiceToggle(
                        option.value,
                        event.target.checked
                      );
                      return;
                    }

                    handleSingleChoiceChange(option.value);
                  }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      ) : null}

      {question.type === "short_text" ? (
        <input
          className="surveys-question-card__input"
          type="text"
          disabled={preview || disabled}
          value={preview ? "" : normalizedValue}
          onChange={(event) => handleTextChange(event.target.value)}
          placeholder={t("surveys_preview_short_text_placeholder")}
        />
      ) : null}

      {question.type === "long_text" ? (
        <textarea
          className="surveys-question-card__textarea"
          disabled={preview || disabled}
          rows={5}
          value={preview ? "" : normalizedValue}
          onChange={(event) => handleTextChange(event.target.value)}
          placeholder={t("surveys_preview_long_text_placeholder")}
        />
      ) : null}

      {errorMessage ? (
        <p className="surveys-question-card__error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </article>
  );
}

export default function Surveys({
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [previewState, setPreviewState] = useState({
    status: "idle",
    survey: null,
    error: "",
  });
  const [liveState, setLiveState] = useState({
    status: "idle",
    metadata: null,
    survey: null,
    alreadySubmitted: false,
    submittedAt: "",
    error: "",
  });
  const [answers, setAnswers] = useState({});
  const [submitPending, setSubmitPending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [questionErrors, setQuestionErrors] = useState({});
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  const previewSurveyKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return normalizeSurveyContentKey(searchParams.get("previewSurvey"));
  }, [location.search]);

  const isPreviewMode = Boolean(previewSurveyKey);

  useEffect(() => {
    let isCancelled = false;

    if (!previewSurveyKey) {
      setPreviewState({
        status: "idle",
        survey: null,
        error: "",
      });
      return () => {
        isCancelled = true;
      };
    }

    setPreviewState({
      status: "loading",
      survey: null,
      error: "",
    });

    loadSurveyContent(previewSurveyKey, i18n.language).then((result) => {
      if (isCancelled) {
        return;
      }

      if (result.ok) {
        setPreviewState({
          status: "ready",
          survey: result.survey,
          error: "",
        });
        return;
      }

      setPreviewState({
        status: "error",
        survey: null,
        error: result.error || "",
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [i18n.language, previewSurveyKey]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    if (isPreviewMode) {
      setLiveState({
        status: "idle",
        metadata: null,
        survey: null,
        alreadySubmitted: false,
        submittedAt: "",
        error: "",
      });
      setAnswers({});
      setSubmitPending(false);
      setSubmitError("");
      setQuestionErrors({});
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    const authToken = getActiveAwareAuthToken(window);

    setLiveState({
      status: "loading",
      metadata: null,
      survey: null,
      alreadySubmitted: false,
      submittedAt: "",
      error: "",
    });
    setSubmitPending(false);
    setSubmitError("");
    setQuestionErrors({});

    async function loadCurrentSurvey() {
      try {
        const payload = await getCurrentSurvey(authToken || "", {
          signal: controller.signal,
        });

        if (cancelled) {
          return;
        }

        if (!payload?.survey) {
          setLiveState({
            status: "empty",
            metadata: null,
            survey: null,
            alreadySubmitted: false,
            submittedAt: "",
            error: "",
          });
          setAnswers({});
          setQuestionErrors({});
          return;
        }

        if (!payload.survey.contentKey) {
          setLiveState({
            status: "error",
            metadata: payload.survey,
            survey: null,
            alreadySubmitted: Boolean(payload.alreadySubmitted),
            submittedAt: payload.submittedAt || "",
            error: "survey_content_unavailable",
          });
          return;
        }

        setLiveState({
          status: "loadingContent",
          metadata: payload.survey,
          survey: null,
          alreadySubmitted: Boolean(payload.alreadySubmitted),
          submittedAt: payload.submittedAt || "",
          error: "",
        });
      } catch (error) {
        if (cancelled || error?.name === "AbortError") {
          return;
        }

        setLiveState({
          status: "error",
          metadata: null,
          survey: null,
          alreadySubmitted: false,
          submittedAt: "",
          error: error?.message || "",
        });
      }
    }

    loadCurrentSurvey();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isPreviewMode]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    if (
      isPreviewMode ||
      !liveState.metadata?.contentKey
    ) {
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    setLiveState((current) => ({
      ...current,
      status: "loadingContent",
      survey: null,
      error: "",
    }));

    loadSurveyContent(liveState.metadata.contentKey, i18n.language, {
      signal: controller.signal,
    }).then((result) => {
      if (cancelled) {
        return;
      }

      if (!result.ok) {
        setLiveState((current) => ({
          ...current,
          status: "error",
          survey: null,
          error: result.error || "",
        }));
        return;
      }

      setLiveState((current) => ({
        ...current,
        status: current.alreadySubmitted ? "submitted" : "ready",
        survey: result.survey,
        error: "",
      }));
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    i18n.language,
    isPreviewMode,
    liveState.metadata?.contentKey,
    liveState.metadata?.id,
  ]);

  useEffect(() => {
    if (!liveState.survey?.questions?.length || isPreviewMode) {
      return;
    }

    setQuestionErrors({});
    setAnswers((currentAnswers) =>
      buildAnswerState(liveState.survey.questions, currentAnswers)
    );
  }, [isPreviewMode, liveState.survey]);

  const headerTitle = useMemo(() => {
    if (isPreviewMode && previewState.status === "ready" && previewState.survey?.title) {
      return previewState.survey.title;
    }

    if (liveState.survey?.title) {
      return liveState.survey.title;
    }

    if (liveState.metadata?.title) {
      return liveState.metadata.title;
    }

    return t("surveys_title");
  }, [
    isPreviewMode,
    liveState.metadata?.title,
    liveState.survey?.title,
    previewState.status,
    previewState.survey?.title,
    t,
  ]);

  const headerDescription = useMemo(() => {
    if (
      isPreviewMode &&
      previewState.status === "ready" &&
      previewState.survey?.description
    ) {
      return previewState.survey.description;
    }

    if (liveState.survey?.description) {
      return liveState.survey.description;
    }

    return t("surveys_intro");
  }, [
    isPreviewMode,
    liveState.survey?.description,
    previewState.status,
    previewState.survey?.description,
    t,
  ]);

  const formattedSubmittedAt = useMemo(
    () => formatSubmittedAt(liveState.submittedAt, i18n.language),
    [i18n.language, liveState.submittedAt]
  );
  const hasUnsavedAnswers = useMemo(
    () =>
      !isPreviewMode &&
      liveState.status === "ready" &&
      hasSurveyAnswerValue(answers),
    [answers, isPreviewMode, liveState.status]
  );

  const closeSurvey = () => {
    navigate("/", { replace: true });
  };

  const handleRequestClose = () => {
    if (hasUnsavedAnswers) {
      setShowCloseConfirm(true);
      return;
    }

    closeSurvey();
  };

  const handleAnswerChange = (question, nextValue) => {
    if (
      question.type === "multiple_choice" &&
      Array.isArray(nextValue) &&
      Number.isInteger(question.maxSelections) &&
      question.maxSelections > 0 &&
      nextValue.length > question.maxSelections
    ) {
      setQuestionErrors((currentErrors) => ({
        ...currentErrors,
        [question.id]: getQuestionLimitError(question),
      }));
      if (submitError) {
        setSubmitError("");
      }
      return;
    }

    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [question.id]: nextValue,
    }));
    setQuestionErrors((currentErrors) => {
      if (!currentErrors[question.id]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[question.id];
      return nextErrors;
    });
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      isPreviewMode ||
      !liveState.survey ||
      submitPending ||
      liveState.status !== "ready"
    ) {
      return;
    }

    const nextQuestionErrors = buildQuestionErrors(
      liveState.survey.questions,
      answers,
      t
    );

    if (Object.keys(nextQuestionErrors).length > 0) {
      setQuestionErrors(nextQuestionErrors);
      const firstError = liveState.survey.questions
        .map((question) => nextQuestionErrors[question.id])
        .find(Boolean);
      setSubmitError(firstError || t("surveys_submit_required"));
      return;
    }

    const authToken = getActiveAwareAuthToken(window);
    if (!authToken && !getCsrfToken()) {
      setSubmitError(t("surveys_auth_required_body"));
      return;
    }

    setSubmitPending(true);
    setSubmitError("");

    try {
      const payload = await submitCurrentSurvey(authToken || "", answers);
      trackProductEvent("survey_submitted", {
        featureArea: "surveys",
        metadata: {
          source: "surveys_page",
          question_count_bucket: getCountBucket(
            liveState.survey.questions?.length || 0
          ),
        },
      });
      setLiveState((current) => ({
        ...current,
        status: "submitted",
        alreadySubmitted: true,
        submittedAt: payload?.submittedAt || current.submittedAt,
      }));
    } catch (error) {
      setSubmitError(getSurveyErrorMessage(t, error, "surveys_submit_failed"));
    } finally {
      setSubmitPending(false);
    }
  };

  const desktopRightRail = enableDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`surveys-layout${
        enableDesktopRightRail ? " surveys-layout--with-right-rail" : ""
      }`}
    >
      <div className="surveys-shell">
        <div className="surveys-page">
        <header className="surveys-header">
          <div className="surveys-header__copy">
            <h1 className="surveys-title">{headerTitle}</h1>
            <p className="surveys-intro">{headerDescription}</p>
          </div>
          <button
            type="button"
            className="surveys-close-button"
            onClick={handleRequestClose}
            aria-label={t("close")}
          >
            <X size={18} strokeWidth={2.4} aria-hidden="true" />
          </button>
        </header>

        {!isPreviewMode && liveState.status === "empty" ? (
          <section
            className="surveys-empty-card"
            aria-label={t("surveys_empty_title")}
          >
            <div className="surveys-empty-card__badge">
              {t("sidebar_survey")}
            </div>
            <h2 className="surveys-empty-card__title">
              {t("surveys_empty_title")}
            </h2>
            <p className="surveys-empty-card__copy">
              {t("surveys_empty_body")}
            </p>
          </section>
        ) : null}

        {isPreviewMode && previewState.status === "loading" ? (
          <section
            className="surveys-status-card surveys-status-card--loading"
            aria-label={t("surveys_preview_loading")}
          >
            <div className="surveys-status-card__badge">
              {t("surveys_preview_badge")}
            </div>
            <h2 className="surveys-status-card__title">
              {t("surveys_preview_loading")}
            </h2>
            <p className="surveys-status-card__copy">
              {t("surveys_preview_loading_body")}
            </p>
          </section>
        ) : null}

        {isPreviewMode && previewState.status === "error" ? (
          <section
            className="surveys-status-card surveys-status-card--error"
            aria-label={t("surveys_preview_load_error_title")}
          >
            <div className="surveys-status-card__badge">
              {t("surveys_preview_badge")}
            </div>
            <h2 className="surveys-status-card__title">
              {t("surveys_preview_load_error_title")}
            </h2>
            <p className="surveys-status-card__copy">
              {t("surveys_preview_load_error_body")}
            </p>
            {previewState.error ? (
              <p className="surveys-status-card__meta">{previewState.error}</p>
            ) : null}
          </section>
        ) : null}

        {isPreviewMode && previewState.status === "ready" ? (
          <section className="surveys-preview-card">
            <div className="surveys-preview-card__badge">
              {t("surveys_preview_badge")}
            </div>

            <div className="surveys-preview-card__notice">
              {t("surveys_preview_notice")}
            </div>

            <div className="surveys-preview-card__questions">
              {previewState.survey.questions.map((question, index) => (
                <SurveyQuestionCard
                  key={question.id}
                  index={index}
                  preview
                  question={question}
                  t={t}
                  errorMessage=""
                  value={getDefaultAnswer(question)}
                />
              ))}
            </div>

            <div className="surveys-preview-card__footer">
              <button className="surveys-preview-card__submit" disabled>
                {previewState.survey.submitLabel || t("surveys_preview_submit")}
              </button>
              <p className="surveys-preview-card__submit-note">
                {t("surveys_preview_submit_disabled")}
              </p>
            </div>
          </section>
        ) : null}

        {!isPreviewMode && liveState.status === "auth" ? (
          <section
            className="surveys-status-card surveys-status-card--error"
            aria-label={t("surveys_auth_required_title")}
          >
            <div className="surveys-status-card__badge">
              {t("sidebar_survey")}
            </div>
            <h2 className="surveys-status-card__title">
              {t("surveys_auth_required_title")}
            </h2>
            <p className="surveys-status-card__copy">
              {t("surveys_auth_required_body")}
            </p>
          </section>
        ) : null}

        {!isPreviewMode &&
        (liveState.status === "loading" || liveState.status === "loadingContent") ? (
          <section
            className="surveys-status-card surveys-status-card--loading"
            aria-label={t("surveys_loading")}
          >
            <div className="surveys-status-card__badge">
              {t("sidebar_survey")}
            </div>
            <h2 className="surveys-status-card__title">
              {t("surveys_loading")}
            </h2>
            <p className="surveys-status-card__copy">
              {t("surveys_loading_body")}
            </p>
          </section>
        ) : null}

        {!isPreviewMode && liveState.status === "error" ? (
          <section
            className="surveys-status-card surveys-status-card--error"
            aria-label={t("surveys_load_error_title")}
          >
            <div className="surveys-status-card__badge">
              {t("sidebar_survey")}
            </div>
            <h2 className="surveys-status-card__title">
              {t("surveys_load_error_title")}
            </h2>
            <p className="surveys-status-card__copy">
              {getSurveyErrorMessage(t, { message: liveState.error }, "surveys_load_error_body")}
            </p>
            {shouldShowSurveyErrorMeta(liveState.error) ? (
              <p className="surveys-status-card__meta">{liveState.error}</p>
            ) : null}
          </section>
        ) : null}

        {!isPreviewMode && liveState.status === "submitted" && liveState.survey ? (
          <section
            className="surveys-status-card surveys-status-card--success"
            aria-label={t("surveys_submitted_title")}
          >
            <div className="surveys-status-card__badge">
              {t("surveys_submitted_badge")}
            </div>
            <h2 className="surveys-status-card__title">
              {t("surveys_submitted_title")}
            </h2>
            <p className="surveys-status-card__copy">
              {liveState.survey.successMessage || t("surveys_submitted_body")}
            </p>
            {formattedSubmittedAt ? (
              <p className="surveys-status-card__meta surveys-status-card__meta--success">
                {t("surveys_submitted_at", { submittedAt: formattedSubmittedAt })}
              </p>
            ) : null}
          </section>
        ) : null}

        {!isPreviewMode && liveState.status === "ready" && liveState.survey ? (
          <form className="surveys-live-card" onSubmit={handleSubmit}>
            <div className="surveys-live-card__badge">{t("sidebar_survey")}</div>
            <div className="surveys-live-card__questions">
              {liveState.survey.questions.map((question, index) => (
                <SurveyQuestionCard
                  key={question.id}
                  index={index}
                  question={question}
                  t={t}
                  errorMessage={questionErrors[question.id] || ""}
                  value={answers[question.id]}
                  disabled={submitPending}
                  onChange={handleAnswerChange}
                />
              ))}
            </div>

            <div className="surveys-live-card__footer">
              {submitError ? (
                <div className="surveys-live-card__error" role="alert">
                  {submitError}
                </div>
              ) : null}

              <button
                className="surveys-live-card__close"
                type="button"
                onClick={handleRequestClose}
                disabled={submitPending}
              >
                {t("close")}
              </button>
              <button
                className="surveys-live-card__submit"
                type="submit"
                disabled={submitPending}
              >
                {submitPending
                  ? t("surveys_submit_submitting")
                  : liveState.survey.submitLabel || t("surveys_submit_button")}
              </button>
            </div>
          </form>
        ) : null}

        {showCloseConfirm ? (
          <div className="surveys-confirm-backdrop" role="presentation">
            <section
              className="surveys-confirm"
              role="dialog"
              aria-modal="true"
              aria-labelledby="surveys-close-confirm-title"
              aria-describedby="surveys-close-confirm-message"
            >
              <h2 id="surveys-close-confirm-title">
                {t("surveys_close_confirm_title")}
              </h2>
              <p id="surveys-close-confirm-message">
                {t("surveys_close_confirm_message")}
              </p>
              <div className="surveys-confirm__actions">
                <button
                  type="button"
                  className="surveys-confirm__button surveys-confirm__button--secondary"
                  onClick={() => setShowCloseConfirm(false)}
                >
                  {t("no")}
                </button>
                <button
                  type="button"
                  className="surveys-confirm__button surveys-confirm__button--danger"
                  onClick={closeSurvey}
                >
                  {t("surveys_close_confirm_yes")}
                </button>
              </div>
            </section>
          </div>
        ) : null}
        </div>
      </div>
      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}
    </div>
  );
}
